import { useId } from 'react';
import { Switch, SwitchProps } from 'react-aria-components';

export const ToggleFieldLayoutValues = {
  Stacked: 'stacked', // label above the switch
  Inline: 'inline', // switch first, label to its right
} as const;

export type ToggleFieldLayout = typeof ToggleFieldLayoutValues[keyof typeof ToggleFieldLayoutValues];

// aria-label names what is toggled when the visible label doesn't, e.g. "HMRC Data" for a switch labelled "On"
export interface ToggleFieldProps extends Pick<SwitchProps, 'aria-label'> {
  label: string,
  description?: string,
  layout?: ToggleFieldLayout,
  isSelected?: boolean,
  defaultSelected?: boolean,
  isDisabled?: boolean,
  onChange?: (isSelected: boolean) => void,
}

const motionClasses = 'duration-state-change ease-out motion-reduce:transition-none';

const trackClasses = `relative h-6 w-11 shrink-0 rounded-full bg-secondary-active transition-[background-color] ${motionClasses} `
  + 'group-selected:bg-primary group-disabled:bg-secondary-hover group-selected:group-disabled:bg-primary/25 '
  + 'group-focus-visible:[outline:-webkit-focus-ring-color_auto_1px] group-focus-visible:outline-offset-2';

const thumbClasses = `absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] transition-transform ${motionClasses} `
  + 'group-selected:translate-x-5 group-disabled:bg-secondary-button group-disabled:shadow-none';

const labelClasses = 'text-body-large text-piptext-default group-disabled:text-piptext-body';

// On/off switch with a question label, built on the react-aria Switch. A disabled switch keeps its value
export default function ToggleField({
  label, description, layout = ToggleFieldLayoutValues.Stacked, isSelected, defaultSelected, isDisabled = false, onChange,
  'aria-label': ariaLabel,
}: Readonly<ToggleFieldProps>) {
  const labelId = useId();
  const descriptionId = useId();
  const isStacked = layout === ToggleFieldLayoutValues.Stacked;

  const text = (
    <span className="flex flex-col gap-1">
      <span id={labelId} className={labelClasses}>{label}</span>
      {description && <span id={descriptionId} className="text-helper text-piptext-body">{description}</span>}
    </span>
  );

  return (
    <Switch
      isSelected={isSelected}
      defaultSelected={defaultSelected}
      isDisabled={isDisabled}
      onChange={onChange}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : labelId}
      aria-describedby={description ? descriptionId : undefined}
      className={`group flex w-fit cursor-pointer disabled:cursor-not-allowed ${isStacked ? 'flex-col items-start gap-2' : 'items-center gap-3'}`}
    >
      {isStacked && text}
      <span aria-hidden="true" className={trackClasses}>
        <span className={thumbClasses} />
      </span>
      {!isStacked && text}
    </Switch>
  );
}
